import React, { useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import HistoryIcon from '@mui/icons-material/History'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'

import Carousel from 'react-multi-carousel'
import { getRecentProducts, removeRecentProduct } from '@/lib/recentProducts'
import RecentProductCard from './RecentProductCard'
import Link from '@/components/Common/Link'

const responsive = {
  desktop: {
    breakpoint: { max: 3000, min: 1280 },
    items: 6,
  },
  tablet: {
    breakpoint: { max: 1280, min: 900 },
    items: 4,
  },
  smallTablet: {
    breakpoint: { max: 900, min: 600 },
    items: 3,
  },
  mobile: {
    breakpoint: { max: 600, min: 0 },
    items: 2,
  },
}

const arrowSx = (side: 'left' | 'right') => ({
  position: 'absolute',
  [side]: -6,
  top: '50%',
  transform: 'translateY(-50%)',
  bgcolor: 'background.paper',
  boxShadow: 3,
  zIndex: 2,
})

/**
 * Dropdown panel for recently viewed products.
 * Rendered by IconMenu in the header.
 */
const FloatingToolbox: React.FC<{
  open: boolean
  onClose: () => void
}> = ({ open, onClose }) => {
  const { formatMessage: t } = useIntl()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [items, setItems] = useState(getRecentProducts())
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'recentProducts_v2') setItems(getRecentProducts())
    }
    const onUpdated = () => setItems(getRecentProducts())
    window.addEventListener('storage', onStorage)
    window.addEventListener('recentProductsUpdated', onUpdated as EventListener)
    return () => {
      window.removeEventListener('storage', onStorage)
      window.removeEventListener('recentProductsUpdated', onUpdated as EventListener)
    }
  }, [])

  // Close on click outside
  useEffect(() => {
    if (!open) return
    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose()
      }
    }
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside)
    }, 100)
    return () => {
      clearTimeout(timer)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open, onClose])

  const handleRemove = (slug: string) => {
    removeRecentProduct(slug)
    setItems(getRecentProducts())
  }

  if (!open || items.length === 0) return null

  return (
    <Paper
      ref={panelRef}
      elevation={0}
      sx={{
        position: 'absolute',
        top: '100%',
        right: 0,
        mt: 1,
        borderRadius: 3,
        overflow: 'hidden',
        boxShadow: '0 8px 32px rgba(0,0,0,0.16), 0 2px 8px rgba(0,0,0,0.08)',
        zIndex: 1500,
        ...(isMobile
          ? {
              position: 'fixed',
              top: 'auto',
              left: 0,
              right: 0,
              bottom: 0,
              mt: 0,
              borderRadius: '16px 16px 0 0',
              maxHeight: '60vh',
              overflowY: 'auto',
            }
          : {
              width: 'calc(100vw - 128px)',
              maxWidth: 1200,
            }),
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: '#fafafa',
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <HistoryIcon fontSize="small" color="primary" />
          {t({
            id: 'web_derniers_produits_consultes',
            defaultMessage: 'Derniers produits consultés',
          })}
        </Typography>
        <Link route="RecentProducts" naked>
          <Typography
            variant="caption"
            sx={{ color: 'primary.main', fontWeight: 600, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
          >
            {items.length} {items.length > 1 ? 'produits' : 'produit'}
          </Typography>
        </Link>
      </Box>

      {/* Carousel */}
      <Box sx={{ position: 'relative', p: 1.5 }}>
        <Carousel
          responsive={responsive}
          ssr
          swipeable
          draggable
          arrows
          infinite={false}
          renderButtonGroupOutside
          customLeftArrow={
            <IconButton size="small" sx={arrowSx('left')}>
              <ChevronLeftIcon />
            </IconButton>
          }
          customRightArrow={
            <IconButton size="small" sx={arrowSx('right')}>
              <ChevronRightIcon />
            </IconButton>
          }
          itemClass="px-1"
          containerClass="px-4"
        >
          {items.map((item) => (
            <RecentProductCard key={item.slug} item={item} onRemove={handleRemove} />
          ))}
        </Carousel>
      </Box>
    </Paper>
  )
}

export default FloatingToolbox

/**
 * Hook to get the count of recent products (for the badge in header).
 */
export function useRecentProductsCount() {
  const [count, setCount] = useState(0)

  useEffect(() => {
    setCount(getRecentProducts().length)

    const refresh = () => setCount(getRecentProducts().length)
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'recentProducts_v2') refresh()
    }

    window.addEventListener('storage', onStorage)
    window.addEventListener('recentProductsUpdated', refresh as EventListener)
    window.addEventListener('focus', refresh)
    return () => {
      window.removeEventListener('storage', onStorage)
      window.removeEventListener('recentProductsUpdated', refresh as EventListener)
      window.removeEventListener('focus', refresh)
    }
  }, [])

  return count
}
